import React, { useContext } from "react";
import { map } from "lodash";
import { Bounds } from "viewport-mercator-project";
import {
  FEATURE_FLAG_NAME,
  OBJECT_TYPE,
  SUPPORT_EMAIL,
} from "common/constants";
import { INFO_PANEL_TAB_NAME } from "common/routing";
import { arrayHasAtLeastOneItem } from "common/utils/arrays";
import { getSubmissionCategoriesForCustomMap } from "common-client/utils/customObject";
import { useGetCustomMapWithGeometryQuery } from "../../generated/graphql";
import useNavigationTab from "../../hooks/useNavigationTabs";
import { TABS } from "../AddressPanel";
import { TabContainer } from "../AddressPanel/__styles__/AddressPanel";
import { AuthContext } from "../Authorization/AuthContext";
import { EmptyState } from "../Common/EmptyState";
import TabGroup from "../Common/TabGroup";
import { MapZoomContext } from "../Maps/contexts/MapZoomContext";
import { LayerContext } from "../Maps/layers";
import { Tasks } from "../Tasks/List";
import { Container, ContentWrapper } from "./__styles__/ObjectInformationPanel";
import Activity from "./Activity";
import Files from "./Files";
import Header from "./Header";
import Overview from "./Overview";
import Records from "./Records";
import { ObjectInfoPanelTabProps } from "./types";

const DEFAULT_OBJECT_TYPE = "Object";
const DEFAULT_OBJECT_NAME = "-";
export const LOADING_TEXT = "Loading...";

const TabBody = (
  props: ObjectInfoPanelTabProps & { tab: INFO_PANEL_TAB_NAME },
) => {
  const { tab, ...childProps } = props;

  return (
    <>
      {tab === INFO_PANEL_TAB_NAME.OVERVIEW && <Overview {...childProps} />}
      {tab === INFO_PANEL_TAB_NAME.ACTIVITY && <Activity {...childProps} />}
      {tab === INFO_PANEL_TAB_NAME.TASKS && (
        <Tasks
          targetObject={props.geometry}
          objectType={OBJECT_TYPE.CUSTOM_MAP_GEOMETRY}
        />
      )}
      {tab === INFO_PANEL_TAB_NAME.RECORDS && <Records {...childProps} />}
      {tab === INFO_PANEL_TAB_NAME.DOCUMENT_UPLOADS && (
        <Files {...childProps} />
      )}
    </>
  );
};

type ObjectInformationPanelProps = {
  customMapId: string;
  geometryId: string;
  fromSavedView?: boolean;
  onClose: () => void;
};

const ObjectInformationPanel: React.FC<ObjectInformationPanelProps> = ({
  customMapId,
  geometryId,
  fromSavedView,
  onClose,
}) => {
  const { account } = useContext(AuthContext);

  let tabs = TABS.filter(tab => {
    if (tab.value === INFO_PANEL_TAB_NAME.WORKFLOWS) {
      return false;
    }

    if (tab.value !== INFO_PANEL_TAB_NAME.TASKS) {
      return true;
    }

    const tasksFeature = account?.featureFlags.find(
      ff => ff.feature === FEATURE_FLAG_NAME.TASKS_WEB,
    );

    if (!tasksFeature) {
      return false;
    }

    return tasksFeature.isEnabled;
  });

  const [tab, setTab] = useNavigationTab({
    defaultTab: tabs[0]!.value,
    allowedTabs: map(tabs, "value"),
  });

  const { toggleLayer, setMarkerPoint } = useContext(LayerContext);
  const { zoomToBounds, zoomToPoint } = useContext(MapZoomContext);

  const { data, loading } = useGetCustomMapWithGeometryQuery({
    variables: { customMapId, geometryId },
    onCompleted: ({ customMap }) => {
      if (customMap) {
        if (!fromSavedView) {
          toggleLayer({
            group: "customMaps",
            id: customMap.id,
            isVisible: true,
          });
        }
        if (arrayHasAtLeastOneItem(customMap.geometries)) {
          const geometry = customMap.geometries[0];
          const pointInGeometry =
            geometry.interiorPoint ?
              {
                longitude: geometry.interiorPoint[0]!,
                latitude: geometry.interiorPoint[1]!,
              }
            : null;
          setMarkerPoint(pointInGeometry);
          if (geometry.bounds) {
            void zoomToBounds(geometry.bounds as Bounds, true);
          } else if (pointInGeometry) {
            void zoomToPoint(pointInGeometry);
          }
        }
      }
    },
  });

  const customMap = data?.customMap;
  const geometry = customMap?.geometries[0];
  const objectType = customMap?.name ?? DEFAULT_OBJECT_TYPE;
  const objectName = geometry?.label ?? DEFAULT_OBJECT_NAME;

  const submissionCategoriesForCustomMap = getSubmissionCategoriesForCustomMap({
    submissionTypes: account?.submissionTypes ?? [],
    customMap,
  });

  // hide records tab if there are no compatible submission types
  if (submissionCategoriesForCustomMap.size === 0) {
    tabs = tabs.filter(tab => tab.value !== INFO_PANEL_TAB_NAME.RECORDS);
  }

  const emptyTitle = `This ${
    customMap ? customMap.name.concat(" object") : "map"
  } is unavailable`;
  return (
    <Container>
      <Header
        loading={loading}
        objectType={objectType}
        objectName={objectName}
        onClose={onClose}
      />
      {geometry ?
        <>
          <TabContainer>
            <TabGroup currentTab={tab} setTab={setTab} tabs={tabs} />
          </TabContainer>
          <TabBody
            tab={tab}
            geometry={geometry}
            customMap={customMap}
            submissionCategories={submissionCategoriesForCustomMap}
          />
        </>
      : loading ?
        <ContentWrapper>
          <em>{LOADING_TEXT}</em>
        </ContentWrapper>
      : <EmptyState
          icon="missing-location"
          title={emptyTitle}
          body="Our features are limited for objects that may have been recently updated or removed. Please wait and try your search again or reach out if the issue continues."
          primaryButtonProps={{
            label: "Contact us",
            onClick: () => window.open(`mailto:${SUPPORT_EMAIL}`),
          }}
        />
      }
    </Container>
  );
};

export default React.memo(ObjectInformationPanel);
